import React, { useState } from 'react';
import { Box, Container, Typography, Button, Stack } from '@mui/material';
import { ArrowUpRight } from '../components/Icons';
import Layout from '../components/Layout';
import ContactFormDialog from '../components/ContactFormDialog';
import { CONTACT, PRODUCT_URLS } from '../constants';

const About: React.FC = () => {
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <Layout>
      <Box
        sx={{
          background:
            'radial-gradient(ellipse 80% 45% at 50% -5%, rgba(14,165,233,0.1), transparent 55%)',
        }}
      >
        <Container maxWidth="lg" sx={{ pt: { xs: 8, md: 11 }, pb: { xs: 6, md: 8 } }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'primary.dark',
              mb: 2,
            }}
          >
            About
          </Typography>
          <Typography
            variant="h1"
            sx={{ maxWidth: 860, mb: 3, fontSize: { xs: '2.4rem', md: '3.6rem' } }}
          >
            We build AI that industries can actually run.
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 18, maxWidth: 640 }}>
            Zendi is a technology company. We design systems that change how sectors work — not
            demos, not slideware. Healthcare is our deepest focus today. It is not the only place we
            intend to go.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 6, md: 10 },
          }}
        >
          <Box>
            <Typography variant="h3" sx={{ mb: 2 }}>
              What we are shipping
            </Typography>
            <Typography sx={{ color: 'text.secondary', mb: 2 }}>
              Companion is our Wellness Companion product for individuals — built around a proprietary
              Eat–Move–Care–Restore wellness model that tracks what people eat, how they move, how
              they care for physical and mental health, and how they restore for longevity. iPOS is
              the clinic platform. AdamCore is the diagnostic expert layer underneath both.
            </Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              Medical aids get white-label member apps from the same Companion base — ParksMed
              is signed; Generation Health is next.
            </Typography>
          </Box>
          <Box>
            <Typography variant="h3" sx={{ mb: 2 }}>
              How we think about it
            </Typography>
            <Typography sx={{ color: 'text.secondary', mb: 2 }}>
              We care about wellness people can live with day to day, clinics that can run on the
              same intelligence, and diagnostic experts that do one job well. Eat–Move–Care–Restore
              is the wellness frame. AdamCore is the clinical expert layer. Generic chat is not the
              product.
            </Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              We also build and sell supporting software and hardware — including ziPOS, our retail
              point-of-sale system — when the work calls for it.
            </Typography>
          </Box>
        </Box>
      </Container>

      <Box sx={{ bgcolor: '#f8fafc', borderTop: '1px solid rgba(15,23,42,0.06)' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
          <Typography variant="h2" sx={{ mb: 5, maxWidth: 640 }}>
            What matters here
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
              gap: 2.5,
            }}
          >
            {[
              {
                title: 'Industry first',
                body: 'We pick hard problems in real sectors and build for them. Healthcare and wellness are where we are deepest now.',
              },
              {
                title: 'Products that ship',
                body: 'Companion and iPOS are live. AdamCore experts run inside both — not a research poster on the wall.',
              },
              {
                title: 'One stack, several products',
                body: 'Wellness for people, tools for clinics, and member builds for medical aids — same Adam foundation.',
              },
            ].map((item) => (
              <Box
                key={item.title}
                sx={{
                  p: 3.5,
                  borderRadius: 4,
                  border: '1px solid rgba(15,23,42,0.08)',
                  bgcolor: '#fff',
                }}
              >
                <Typography variant="h4" sx={{ mb: 1.25 }}>
                  {item.title}
                </Typography>
                <Typography sx={{ color: 'text.secondary' }}>{item.body}</Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 10, md: 14 }, textAlign: 'center' }}>
        <Typography variant="h2" sx={{ mb: 2 }}>
          Want to talk?
        </Typography>
        <Typography sx={{ color: 'text.secondary', mb: 4 }}>
          {CONTACT.email} · {CONTACT.phone}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
          <Button variant="contained" size="large" onClick={() => setContactOpen(true)}>
            Get in touch
          </Button>
          <Button
            variant="outlined"
            size="large"
            endIcon={<ArrowUpRight />}
            component="a"
            href={PRODUCT_URLS.companion}
            target="_blank"
            rel="noopener noreferrer"
          >
            Companion
          </Button>
        </Stack>
      </Container>

      <ContactFormDialog
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        title="Contact Zendi"
        description="Partnerships, AdamCore, product rollouts, or something new."
      />
    </Layout>
  );
};

export default About;
